import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'

import ArrowEndUpIcon from '@offerzen/zenicon/ArrowEndUpIcon'
import TrashIcon from '@offerzen/zenicon/TrashIcon'
import UserIcon from '@offerzen/zenicon/UserIcon'
import AccessibleIcon from '../AccessibleIcon'
import BadgeButton from '../BadgeButton'
import Badge from '../Badge'
import FormErrorMessage from '../FormErrorMessage'
import FormHelpText from '../FormHelpText'

import { colors } from '../../themes'

import styles from './styles'

const DEFAULT_IMAGE_SIZE_MB = 10

const ImageUpload = ({
  url,
  helpText,
  sizeLimit = DEFAULT_IMAGE_SIZE_MB,
  meta = {},
  onChange,
  name,
  id,
  label = 'Upload an image',
  className,
  alt,
}) => {
  const [error, setError] = useState()
  const [image, setImage] = useState(url)

  const selectFile = event => {
    const file = event.target.files[0]

    const sizeInBytes = sizeLimit * 1000000
    if (file.size >= sizeInBytes) {
      setError(`File exceeds the ${sizeLimit}MB limit`)
      return
    }

    const acceptedTypes = ['.jpg', '.jpeg', '.png']
    const hasAcceptedType = acceptedTypes.some(type => file.name.endsWith(type))
    if (!hasAcceptedType) {
      setError(`File format should be ${acceptedTypes.slice(0, -1).join(', ')} or ${acceptedTypes.slice(-1)}`)
      return
    }

    setImage(URL.createObjectURL(file))
    setError(null)
    if (onChange) onChange(file)
  }

  const removeFile = () => {
    setImage(null)
    setError(null)
    if (onChange) onChange(null)
  }

  const errored = (meta.touched && meta.error) || error

  return (
    <div className={cx(className, styles.wrapper)}>
      <figure className={cx(styles.imageUpload, errored && styles.errorLabel)}>
        {image ? (
          <>
            <div className={styles.hoverOverlay}>
              <label htmlFor={id}>
                <Badge icon={ArrowEndUpIcon} className={styles.buttonHoverState} />
                <span className={styles.accessiblyHidden}>{label}</span>
                <input className={styles.accessiblyHidden} id={id} name={name || id} type="file" accept="image/*" onChange={selectFile} />
              </label>
              <BadgeButton icon={TrashIcon} onClick={removeFile} />
            </div>
            <img src={image} alt={alt} className={cx(styles.imagePreview)} />
          </>
        ) : (
          <label htmlFor={id} className={styles.emptyImage}>
            <AccessibleIcon icon={UserIcon} size={28} color={colors.cornflowerBlue} />
            {label}
            <input className={styles.accessiblyHidden} id={id} name={name || id} type="file" accept="image/*" onChange={selectFile} />
          </label>
        )}
      </figure>
      {Boolean(helpText && !errored) && <FormHelpText text={helpText} />}
      {Boolean(errored) && <FormErrorMessage text={error || meta.error} />}
    </div>
  )
}

ImageUpload.propTypes = {
  url: PropTypes.string,
  props: PropTypes.shape({
    required: PropTypes.string,
  }),
  helpText: PropTypes.string,
  sizeLimit: PropTypes.number,
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  onChange: PropTypes.func,
  className: PropTypes.string,
  alt: PropTypes.string,
}

export default ImageUpload
